import { Bot, Info, UserPen } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import { useDocumentVariables } from '../api'
import { FIELD_TYPES, type DocField } from '../types'

type Props = { field: DocField; onChange: (field: DocField) => void }

const SOURCES = [
  { value: 'otomatik', icon: Bot },
  { value: 'personel', icon: UserPen },
] as const

export function FieldPropsForm({ field, onChange }: Props) {
  const { t } = useTranslation()
  const variables = useDocumentVariables().data ?? []

  return (
    <div className="space-y-3">
      <Field id="ft" label={t('hr.belgeler.field.type')}>
        <Select
          id="ft"
          value={field.type}
          onChange={(type) => onChange({ ...field, type })}
          options={FIELD_TYPES.map((ft) => ({ value: ft, label: t(`hr.belgeler.fieldTypes.${ft}`) }))}
        />
      </Field>

      <Field id="fl" label={t('hr.belgeler.field.label')}>
        <Input id="fl" value={field.label} onChange={(e) => onChange({ ...field, label: e.target.value })} />
      </Field>

      <Field id="fk" label={t('hr.belgeler.field.key')}>
        <Input
          id="fk"
          className="font-mono text-xs"
          value={field.key}
          onChange={(e) => onChange({ ...field, key: e.target.value.replace(/\s+/g, '_') })}
        />
      </Field>

      <div className="space-y-1.5">
        <Label className="text-xs">{t('hr.belgeler.field.whoFills')}</Label>
        <div role="radiogroup" className="grid grid-cols-2 gap-1 rounded-lg border bg-muted/40 p-1">
          {SOURCES.map(({ value, icon: Icon }) => {
            const checked = field.source === value
            return (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={checked}
                onClick={() =>
                  onChange({
                    ...field,
                    source: value,
                    variable: value === 'otomatik' ? field.variable ?? variables[0]?.key : undefined,
                  })
                }
                className={cn(
                  'flex min-w-0 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
                  checked ? 'bg-card text-primary shadow-xs ring-1 ring-primary/20' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Icon className="size-3.5 shrink-0" />
                <span className="truncate">{t(`hr.belgeler.field.sourceShort.${value}`)}</span>
              </button>
            )
          })}
        </div>
      </div>

      {field.source === 'otomatik' ? (
        <Field id="fv" label={t('hr.belgeler.field.variable')}>
          <Select
            id="fv"
            className="font-mono text-xs"
            value={field.variable ?? ''}
            onChange={(variable) => onChange({ ...field, variable })}
            options={[
              { value: '', label: t('hr.belgeler.field.selectVar') },
              ...variables.map((v) => ({ value: v.key, label: `{{${v.key}}}`, description: v.label, keywords: [v.label] })),
            ]}
          />
        </Field>
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        <Field id="freq" label={t('hr.belgeler.field.required')}>
          <Select
            id="freq"
            value={field.required ? 'yes' : 'no'}
            onChange={(v) => onChange({ ...field, required: v === 'yes' })}
            options={[
              { value: 'yes', label: t('hr.belgeler.field.yes') },
              { value: 'no', label: t('hr.belgeler.field.no') },
            ]}
          />
        </Field>
        <Field id="fval" label={t('hr.belgeler.field.validation')}>
          <Select
            id="fval"
            value={field.validation ?? ''}
            onChange={(v) => onChange({ ...field, validation: v || undefined })}
            options={[
              { value: '', label: t('hr.belgeler.field.validationNone') },
              { value: 'telefon', label: t('hr.belgeler.field.validationPhone') },
              { value: 'eposta', label: t('hr.belgeler.field.validationEmail') },
              { value: 'sayi', label: t('hr.belgeler.field.validationNumber') },
            ]}
          />
        </Field>
      </div>

      <Field id="fph" label={t('hr.belgeler.field.placeholder')}>
        <Input id="fph" value={field.placeholder ?? ''} onChange={(e) => onChange({ ...field, placeholder: e.target.value })} />
      </Field>

      <Field id="ferr" label={t('hr.belgeler.field.errorMessage')}>
        <Textarea id="ferr" rows={2} value={field.errorMessage ?? ''} onChange={(e) => onChange({ ...field, errorMessage: e.target.value })} />
      </Field>

      <div className="flex gap-2 rounded-lg border border-primary/15 bg-primary/[0.04] p-2.5 text-xs leading-relaxed text-foreground/80">
        <Info className="mt-0.5 size-3.5 shrink-0 text-primary" />
        <p>{field.source === 'personel' ? t('hr.belgeler.right.manualFieldNote') : t('hr.belgeler.right.autoFieldNote')}</p>
      </div>
    </div>
  )
}

function Field({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 space-y-1.5">
      <Label htmlFor={id} className="block truncate text-xs">
        {label}
      </Label>
      {children}
    </div>
  )
}
